import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useRecoilValue} from 'recoil';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import {userInfoState} from 'utils/Atoms';
import CommonModal from 'components/common/CommonModal';
import DefaultText from 'components/common/DefaultText';
import DefaultButton from 'components/common/DefaultButton';
import DefaultInput from 'components/common/DefaultInput';
import DefaultImageView from 'components/common/DefaultImageView';
import LabelInput from 'components/common/LabelInput';
import {
  searchHqManagerCandidates,
  createHqSeedGrant,
  createHqInvitedGrant,
} from 'function/api';
import {formatPhoneNumber} from 'utils/fomatPhoneNumber';

type Props = {
  contractor: {uuid: string; name: string};
  closeModal: () => void;
  onGranted: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setErrorModal: React.Dispatch<React.SetStateAction<{}>>;
};

const HqGrantAddModal = ({
  contractor,
  closeModal,
  onGranted,
  setIsLoading,
  setErrorModal,
}: Props) => {
  const userInfo = useRecoilValue(userInfoState);
  const [keyword, setKeyword] = useState('');
  const [searchResult, setSearchResult] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<null | any>(null);
  const [grantRole, setGrantRole] = useState<'SEED' | 'INVITED'>('SEED');
  const [reason, setReason] = useState('');

  // 시공사 관리자 후보 자동 로드 (모달 열릴 때 + keyword 변경 시).
  // 응답의 contractorPermissions 각 항목 = { uuid, role, user: {uuid, phone, profile} }
  const fetchCandidates = (kw = '') => {
    searchHqManagerCandidates({
      userInfo,
      setErrorModal,
      setIsLoading,
      contractorUUID: contractor.uuid,
      keyword: kw,
      page: 1,
      count: 50,
    }).then((result: any) => {
      const perms = result?.contractorPermissions ?? [];
      // 같은 user 가 OWNER + CO_OWNER 등 여러 role 보유 가능 → user.uuid 로 중복 제거
      const uniqueByUser = new Map<string, any>();
      perms.forEach((p: any) => {
        const u = p?.user;
        if (!u?.uuid) return;
        const existing = uniqueByUser.get(u.uuid);
        if (existing) {
          // 이미 추가된 user 면 role 합치기
          existing.roles = Array.from(new Set([...(existing.roles ?? []), p.role]));
        } else {
          uniqueByUser.set(u.uuid, {
            ...u,
            roles: [p.role],
          });
        }
      });
      setSearchResult(Array.from(uniqueByUser.values()));
    });
  };

  // 모달 열릴 때 자동으로 후보 전체 표시.
  useEffect(() => {
    fetchCandidates('');
  }, [contractor.uuid]); // eslint-disable-line

  const handleSearch = () => fetchCandidates(keyword.trim());

  const handleGrant = async () => {
    if (!selectedUser) {
      alert('user 를 선택하세요');
      return;
    }
    const fn = grantRole === 'SEED' ? createHqSeedGrant : createHqInvitedGrant;
    const result = await fn({
      userInfo,
      setErrorModal,
      setIsLoading,
      contractorUUID: contractor.uuid,
      targetUserUUID: selectedUser.uuid,
      reason: reason || undefined,
    });
    if (result) {
      onGranted();
    }
  };

  return (
    <CommonModal closeModal={closeModal}>
      <ModalContainer>
        <ModalHeader>
          <DefaultText
            text={`HQ 접근 권한 부여 — ${contractor.name}`}
            textStyle={{fontSize: minmax(28), fontWeight: 'bold'}}
          />
          <DefaultImageView
            source="images/common/close.png"
            onClick={closeModal}
            style={{
              width: minmax(28),
              height: minmax(28),
              cursor: 'pointer',
            }}
          />
        </ModalHeader>

        <Section>
          <DefaultText text="역할 선택" textStyle={{fontSize: minmax(16), fontWeight: 600, marginBottom: minmax(8)}} />
          <RoleRow>
            <RoleOption
              $active={grantRole === 'SEED'}
              onClick={() => setGrantRole('SEED')}>
              <DefaultText text="SEED" textStyle={{fontSize: minmax(16), fontWeight: 'bold'}} />
              <DefaultText
                text="운영팀 직접 부여 (1차 권한)"
                textStyle={{fontSize: minmax(12), color: colors.darkGray}}
              />
            </RoleOption>
            <RoleOption
              $active={grantRole === 'INVITED'}
              onClick={() => setGrantRole('INVITED')}>
              <DefaultText text="INVITED" textStyle={{fontSize: minmax(16), fontWeight: 'bold'}} />
              <DefaultText
                text="안전관리자 등 (1단계)"
                textStyle={{fontSize: minmax(12), color: colors.darkGray}}
              />
            </RoleOption>
          </RoleRow>
        </Section>

        <Section>
          <DefaultText
            text="대상 user 선택 (시공사 등록 OWNER/CO_OWNER/MANAGER)"
            textStyle={{fontSize: minmax(16), fontWeight: 600, marginBottom: minmax(8)}}
          />
          <SearchRow>
            <SearchBox>
              <DefaultInput
                value={keyword}
                setValue={(e: any) => setKeyword(e)}
                placeholderText="이름/전화번호 필터 (비우면 전체)"
                wrapperStyle={{height: '100%'}}
                onKeyPress={(e: any) => {
                  if (e.key === 'Enter') handleSearch();
                }}
              />
            </SearchBox>
            <SearchButton onClick={handleSearch}>검색</SearchButton>
          </SearchRow>

          {searchResult.length > 0 && (
            <UserList>
              {searchResult.map((u: any) => (
                <UserRow
                  key={u.uuid}
                  $selected={selectedUser?.uuid === u.uuid}
                  onClick={() => setSelectedUser(u)}>
                  <DefaultText
                    text={`${u.profile?.name || u.name || '-'}  [${(u.roles ?? []).join(', ')}]`}
                    textStyle={{fontSize: minmax(15), fontWeight: 600}}
                  />
                  <DefaultText
                    text={formatPhoneNumber({phone: u.phone, locale: u.locale}) || '-'}
                    textStyle={{fontSize: minmax(13), color: colors.darkGray}}
                  />
                  <DefaultText
                    text={u.uuid}
                    textStyle={{fontSize: minmax(11), color: colors.darkGray}}
                  />
                </UserRow>
              ))}
            </UserList>
          )}

          {selectedUser && (
            <Selected>
              ✅ 선택됨: {selectedUser.profile?.name || selectedUser.name || selectedUser.uuid}
              {' '}({formatPhoneNumber({phone: selectedUser.phone, locale: selectedUser.locale}) || '-'})
            </Selected>
          )}
        </Section>

        <Section>
          <LabelInput
            type={'text'}
            labelText={'사유 (선택)'}
            value={reason}
            setValue={setReason}
          />
        </Section>

        <DefaultButton
          text="권한 부여"
          actived={selectedUser ? 1 : 0}
          buttonStyle={{
            width: '100%',
            backgroundColor: selectedUser ? colors.primary2 : colors.lightGrey,
            marginTop: minmax(20),
          }}
          textStyle={{fontSize: minmax(20), color: colors.white}}
          onClick={handleGrant}
        />
      </ModalContainer>
    </CommonModal>
  );
};

export default HqGrantAddModal;

const ModalContainer = styled.div`
  background: ${colors.white};
  padding: ${minmax(30)};
  border-radius: ${minmax(10)};
  width: ${minmax(680)};
  max-height: 80vh;
  overflow-y: auto;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${colors.lightGrey};
  padding-bottom: ${minmax(15)};
  margin-bottom: ${minmax(20)};
`;
const Section = styled.div`
  margin-bottom: ${minmax(20)};
`;
const RoleRow = styled.div`
  display: flex;
  gap: ${minmax(12)};
`;
const RoleOption = styled.div<{$active: boolean}>`
  flex: 1;
  padding: ${minmax(14)};
  border: 2px solid ${(p) => (p.$active ? colors.primary : colors.lightGrey)};
  border-radius: ${minmax(8)};
  background: ${(p) => (p.$active ? colors.whisper : colors.white)};
  cursor: pointer;
`;
const SearchRow = styled.div`
  display: flex;
  gap: ${minmax(8)};
  align-items: center;
`;
const SearchBox = styled.div`
  flex: 1;
  height: ${minmax(40)};
`;
const SearchButton = styled.button`
  background: ${colors.primary};
  color: white;
  border: none;
  padding: 0 ${minmax(20)};
  border-radius: ${minmax(6)};
  cursor: pointer;
  height: ${minmax(40)};
`;
const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${minmax(4)};
  margin-top: ${minmax(10)};
  max-height: ${minmax(240)};
  overflow-y: auto;
`;
const UserRow = styled.div<{$selected?: boolean}>`
  padding: ${minmax(10)};
  border: 1px solid ${(p) => (p.$selected ? colors.primary : colors.lightGrey)};
  border-radius: ${minmax(6)};
  background: ${(p) => (p.$selected ? colors.whisper : colors.white)};
  cursor: pointer;
  &:hover {
    background: ${colors.whisper};
  }
`;
const Selected = styled.div`
  margin-top: ${minmax(10)};
  padding: ${minmax(10)};
  background: ${colors.whisper};
  border-radius: ${minmax(4)};
  color: ${colors.primary};
  font-weight: bold;
`;
